"use client";
import { createContext, useContext, useEffect, useRef } from "react";

import type { Sound } from "../types";

import { soundEnded, soundLoaded, soundProgress, soundStarted } from "../state/actions";
import { useSoundboard } from "../state/reducer";
import { AudioPlayerInstance } from "./AudioPlayerInstance";

// Context for audio playback
interface AudioContextType {
  clearPlayingSounds: () => void;
  getSound: (soundId: string) => AudioPlayerInstance | undefined;
  isPlaying: (soundId: string) => boolean;
  queueSound: (soundId: string) => void;
  stopSound: (soundId: string) => void;
}

const AudioContext = createContext<AudioContextType | null>(null);

export const useAudio = () => {
  const context = useContext(AudioContext);
  if (!context) {
    throw new Error("useAudio must be used within an AudioProvider");
  }
  return context;
};

export const AudioPlayer = ({ children }: { children: React.ReactNode }) => {
  const { state, dispatch } = useSoundboard();
  const playersRef = useRef<Map<string, AudioPlayerInstance>>(new Map());

  // Clean up and initialize audio players when the active preset changes
  useEffect(() => {
    const activePreset = state.presets[state.activePresetId!];
    if (!activePreset) return;

    // Get current sound IDs in the active preset
    const currentSoundIds = new Set(
      activePreset.sounds.filter((sound): sound is Sound => sound.type === "sound").map(sound => sound.id),
    );

    // Clean up players that are no longer in the active preset
    playersRef.current.forEach((player, soundId) => {
      if (!currentSoundIds.has(soundId)) {
        player.unload();
        playersRef.current.delete(soundId);
      }
    });

    // Create or update audio players for the active preset
    activePreset.sounds
      .filter((sound): sound is Sound => sound.type === "sound")
      .forEach(sound => {
        const existingPlayer = playersRef.current.get(sound.id);

        // Only create a new player if one doesn't exist or if the URL is changed
        if (!existingPlayer || existingPlayer.sound?.url !== sound.url) {
          // Clean up existing player if it exists
          if (existingPlayer) {
            existingPlayer.unload();
          }

          const player = new AudioPlayerInstance(
            sound.url,
            {
              onLoad: duration => {
                dispatch(soundLoaded({ soundId: sound.id, duration }));
              },
              onPlay: () => {
                dispatch(soundStarted({ soundId: sound.id }));
              },
              onProgress: currentTime => {
                dispatch(soundProgress({ soundId: sound.id, currentTime }));
              },
              onEnd: () => {
                if (!sound.isLooped) {
                  dispatch(soundEnded({ soundId: sound.id }));
                }
              },
              onStop: () => {
                dispatch(soundEnded({ soundId: sound.id }));
              },
            },
            sound.isLooped,
            sound,
          );
          playersRef.current.set(sound.id, player);
        }

        if (existingPlayer && existingPlayer.sound?.isLooped !== sound.isLooped) {
          existingPlayer.loop(!!sound.isLooped);
        }
      });
  }, [state.activePresetId, state.presets, dispatch]);

  useEffect(() => {
    cleanup();
    return () => {
      cleanup();
    };
  }, []);

  useEffect(() => {
    Howler.volume(state.volume);
  }, [state.volume]);

  const cleanup = () => {
    // Unload all players
    playersRef.current.forEach(player => player.unload());
    playersRef.current.clear();
  };

  const getSound = (soundId: string) => {
    return playersRef.current.get(soundId);
  };

  const queueSound = (soundId: string) => {
    const player = getSound(soundId);
    if (!player) return;
    if (state.isPaused) {
      player.prepareForPlay();
    } else {
      player.play();
    }
  };

  const stopSound = (soundId: string) => {
    const player = getSound(soundId);
    if (player) {
      player.stop();
    }
  };

  const isPlaying = (soundId: string) => {
    const player = getSound(soundId);
    return player ? player.playing() : false;
  };

  const pauseAll = () => {
    playersRef.current.forEach(player => {
      if (player.playing()) {
        player.pause();
      }
    });
  };

  const resumeAll = () => {
    playersRef.current.forEach(player => {
      if (player) {
        player.resume();
      }
    });
  };

  const clearPlayingSounds = () => {
    playersRef.current.forEach(player => {
      player.stop();
    });
  };

  useEffect(() => {
    if (state.isPaused) {
      pauseAll();
    } else {
      resumeAll();
    }
  }, [state.isPaused]);

  return (
    <AudioContext.Provider
      value={{
        getSound,
        isPlaying,
        queueSound,
        stopSound,
        clearPlayingSounds,
      }}
    >
      {children}
    </AudioContext.Provider>
  );
};
